import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/modern/components/ui/tabs";
import { BaseModal } from "../base/BaseModal";
import { BaseModalBody } from "../base/BaseModalBody";
import { LeaderboardUserCard } from "./components/LeaderboardUserCard";
import BryntumContext from "@/react/context/bryntumContext";
import { useContext, useMemo, useState } from "react";
import { INCENTIVE_PERMISSION_KEYS, INCENTIVE_POINT_STATUS } from "@/other/constants/IncentiveKeys";
import { DateHelper } from "@bryntum/schedulerpro";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const MODAL_SIZE_CONFIG = {
  width: "600px",
  height: "600px",
  minHeight: "600px",
  minWidth: "600px",
  maxHeight: "800px",
  maxWidth: "800px"
};

const getPointsData = (project, filterFn = () => true) => {
  const { incentivePointsStore } = project;
  const data = incentivePointsStore.query((record) => !record.isGroupHeader && !record.isLinked && !record.archived && filterFn(record), true);

  // Single pass through data to group by user and calculate totals
  const groupedData = data.reduce((acc, record) => {
    const userId = record.boardUserId;

    if (!acc[userId]) {
      acc[userId] = {
        boardUser: record.boardUser,
        totalPoints: 0,
        pointsRecordCount: 0,
        points: []
      };
    }

    // Add to totals and points array in single pass
    acc[userId].totalPoints += record.points;
    acc[userId].pointsRecordCount += 1;
    acc[userId].points.push({ pointRecord: record });

    return acc;
  }, {});

  // Sort points for each user and return sorted users by total points
  return Object.values(groupedData)
    .map(userData => ({
      ...userData,
      points: userData.points.sort((a, b) => b.pointRecord.points - a.pointRecord.points)
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints);
}

const getFooterData = (activeTab, totalPointsData, approvedPointsData, awardedPointsData) => {
  switch (activeTab) {
    case "total":
      return {
        totalUsers: totalPointsData.length,
        totalPoints: totalPointsData.reduce((sum, pointsData) => sum + pointsData.totalPoints, 0)
      };
    case "approved":
      return {
        totalUsers: approvedPointsData.length,
        totalPoints: approvedPointsData.reduce((sum, pointsData) => sum + pointsData.totalPoints, 0)
      };
    case "awarded":
      return {
        totalUsers: awardedPointsData.length,
        totalPoints: awardedPointsData.reduce((sum, pointsData) => sum + pointsData.totalPoints, 0)
      };
  }
}

export const SwivuPointsLeaderboardPopup = ({ record, onClose, editorInstance }) => {
  const { project } = useContext(BryntumContext);
  const { version } = useSwivuStore({ storeId: STORE_KEYS.incentivePointsStore });

  const { APPROVED_POINTS_VIEW, AWARDED_POINTS_VIEW } = INCENTIVE_PERMISSION_KEYS;
  const [activeTab, setActiveTab] = useState(() => {
    if (checkIncentivePermission({ project, permissionKey: AWARDED_POINTS_VIEW }) && checkIncentivePermission({ project, permissionKey: APPROVED_POINTS_VIEW })) return "total";
    if (checkIncentivePermission({ project, permissionKey: AWARDED_POINTS_VIEW })) return "awarded";
    if (checkIncentivePermission({ project, permissionKey: APPROVED_POINTS_VIEW })) return "approved";
    return "total";
  });

  const memoizedPermissionMap = useMemo(() => {
    const permissionKeys = [
      APPROVED_POINTS_VIEW,
      AWARDED_POINTS_VIEW
    ];
    return permissionKeys.reduce((acc, permissionKey) => {
      acc[permissionKey] = checkIncentivePermission({ project, permissionKey });
      return acc;
    }, {});
  }, [project]);

  const pointsData = useMemo(() => {
    const approvedPointsData = !memoizedPermissionMap[APPROVED_POINTS_VIEW]
      ? []
      : getPointsData(project, (record) => record.status === INCENTIVE_POINT_STATUS.APPROVED);

    const awardedPointsData = !memoizedPermissionMap[AWARDED_POINTS_VIEW]
      ? []
      : getPointsData(project, (record) => record.status === INCENTIVE_POINT_STATUS.AWARDED);

    const totalPointsData = (!memoizedPermissionMap[AWARDED_POINTS_VIEW] || !memoizedPermissionMap[APPROVED_POINTS_VIEW])
      ? []
      : getPointsData(project, (record => record.status === INCENTIVE_POINT_STATUS.APPROVED || record.status === INCENTIVE_POINT_STATUS.AWARDED), true);

    return {
      approved: approvedPointsData,
      awarded: awardedPointsData,
      total: totalPointsData
    };
  }, [project, memoizedPermissionMap, version]);

  const footerData = useMemo(() => {
    return getFooterData(activeTab, pointsData.total, pointsData.approved, pointsData.awarded);
  }, [activeTab, pointsData, version]);

  const lastUpdatedPointsDate = useMemo(() => {
    const { incentivePointsStore } = project;
    const lastUpdatedPoint = incentivePointsStore.query((record) => record.status === INCENTIVE_POINT_STATUS.APPROVED || INCENTIVE_POINT_STATUS.AWARDED).sort((a, b) => b.updatedAt - a.updatedAt)[0];
    return lastUpdatedPoint?.updatedAt;
  }, [project, version])

  return (
    <BaseModal
      title={"Leaderboard"}
      onClose={onClose}
      modal={true}
      record={record}
      size={MODAL_SIZE_CONFIG}
      footerRenderer={() => <Footer totalUsers={footerData.totalUsers} totalPoints={footerData.totalPoints} lastUpdated={DateHelper.format(new Date(lastUpdatedPointsDate), "MM/DD/YYYY")} />}
      hideMenu={true}
      headerIconCls="fa-regular fa-trophy !text-yellow-500"
      editorInstance={editorInstance}
    >
      <BaseModalBody>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-swivu-base">
            <TabsTrigger disabled= {!memoizedPermissionMap[AWARDED_POINTS_VIEW] || !memoizedPermissionMap[APPROVED_POINTS_VIEW]} className="flex-1" value="total">
              Total Points
            </TabsTrigger>
            <TabsTrigger disabled= {!memoizedPermissionMap[APPROVED_POINTS_VIEW]} className="flex-1" value="approved">
              Approved
            </TabsTrigger>
            <TabsTrigger disabled= {!memoizedPermissionMap[AWARDED_POINTS_VIEW]} className="flex-1" value="awarded">
              Awarded
            </TabsTrigger>
          </TabsList>

          {memoizedPermissionMap[AWARDED_POINTS_VIEW] && memoizedPermissionMap[APPROVED_POINTS_VIEW] && (
            <TabsContent className="space-y-swivu-vertical" value="total">
              <p className="text-swivu-sm text-muted-foreground m-0">
                Combined approved and awarded points for overall ranking
              </p>
              {pointsData.total.map((userData, index) => (
                <LeaderboardUserCard key={index} rank={index + 1} pointsData={userData} showStatus={true} />
              ))}
              {pointsData.total.length === 0 && <p className="text-xs text-muted-foreground m-0">No results found</p>}
            </TabsContent>
          )}
          {memoizedPermissionMap[APPROVED_POINTS_VIEW] && (
            <TabsContent className="space-y-swivu-vertical" value="approved">
              <p className="text-swivu-sm text-muted-foreground m-0">
                Points that have been approved but not yet awarded
              </p>
              {pointsData.approved.map((userData, index) => (
                <LeaderboardUserCard key={index} rank={index + 1} pointsData={userData} />
              ))}
              {pointsData.approved.length === 0 && <p className="text-xs text-muted-foreground m-0 text-center">No results found</p>}
            </TabsContent>
          )}
          {memoizedPermissionMap[AWARDED_POINTS_VIEW] && (
            <TabsContent className="space-y-swivu-vertical" value="awarded">
              <p className="text-swivu-sm text-muted-foreground m-0">
                Points that have been officially awarded to users
              </p>
              {pointsData.awarded.map((userData, index) => (
                <LeaderboardUserCard key={index} rank={index + 1} pointsData={userData} />
              ))}
              {pointsData.awarded.length === 0 && <p className="text-xs text-muted-foreground m-0">No results found</p>}
            </TabsContent>
          )}
        </Tabs>
      </BaseModalBody>
    </BaseModal>
  );
};

const Footer = ({totalUsers, totalPoints, lastUpdated}) => {
  return (
    <div className="flex justify-between text-base text-muted-foreground w-full">
      <div className="flex flex-wrap gap-swivu-base">
        <span>
          <strong className="text-foreground">{totalUsers}</strong> users
        </span>
        <span>
          <strong className="text-foreground">{totalPoints}</strong> total points
        </span>
      </div>
      <p className="m-0">Last updated: {lastUpdated}</p>
    </div>
  );
};
